import { useCallback, useState } from 'react';
import { Paper, Stack, Typography } from '@mui/material';

import { ChecklistStep } from './DashboardChecklist.interface';
import { ChecklistProgressBar, ChecklistRow } from './DashboardChecklist.styles';
import ChecklistStepRow from './ChecklistStepRow';

interface DashboardChecklistCardProps {
    steps: ChecklistStep[];
    onStart: (step: ChecklistStep) => void;
}

function DashboardChecklistCard({ steps, onStart }: Readonly<DashboardChecklistCardProps>) {
    const [expandedStepIds, setExpandedStepIds] = useState<Set<string>>(new Set());

    const completedCount = steps.filter((step) => step.completed).length;
    const progressLabel = `${completedCount} of ${steps.length} steps completed`;

    const toggleStep = useCallback((stepId: string) => {
        setExpandedStepIds((prev) => {
            const next = new Set(prev);
            if (next.has(stepId)) {
                next.delete(stepId);
            } else {
                next.add(stepId);
            }
            return next;
        });
    }, []);

    return (
        <Paper variant="outlined" sx={{ width: '100%', maxWidth: 900, alignSelf: 'center', overflow: 'hidden' }}>
            <ChecklistRow direction="row" spacing={2} alignItems="center" sx={{ p: 3 }}>
                <Typography variant="body1paragraph" noWrap>
                    {progressLabel}
                </Typography>
                <ChecklistProgressBar
                    variant="determinate"
                    value={steps.length ? (completedCount / steps.length) * 100 : 0}
                    aria-label={progressLabel}
                />
            </ChecklistRow>
            <Stack>
                {steps.map((step, index) => (
                    <ChecklistStepRow
                        key={step.id}
                        step={step}
                        index={index}
                        isExpanded={expandedStepIds.has(step.id)}
                        onToggle={toggleStep}
                        onStart={onStart}
                    />
                ))}
            </Stack>
        </Paper>
    );
}

export default DashboardChecklistCard;
